import {
  Card,
  Flex,
  Icon,
  Input,
  Button,
  Select,
  Heading,
  FormLabel,
  IconButton,
  InputGroup,
  FormControl,
  InputRightElement
} from '@homebotapp/hb-react-component-catalog'
import { useState } from 'react'
import { useIntl, defineMessages } from 'react-intl'

export const MSG = defineMessages({
  loanDetails: {
    id: 'AffordabilityCalculator.loanDetails',
    defaultMessage: 'Loan details'
  },
  loanType: {
    id: 'AffordabilityCalculator.loanType',
    defaultMessage: 'Loan type'
  },
  interestRate: {
    id: 'AffordabilityCalculator.interestRate',
    defaultMessage: 'Interest rate'
  },
  loanProgram30Fixed: {
    id: 'AffordabilityCalculator.loanProgram30Fixed',
    defaultMessage: '30-year fixed'
  },
  loanProgram15Fixed: {
    id: 'AffordabilityCalculator.loanProgram15Fixed',
    defaultMessage: '15-year fixed'
  },
  show: {
    id: 'AffordabilityCalculator.show',
    defaultMessage: 'Show'
  },
  hide: {
    id: 'AffordabilityCalculator.hide',
    defaultMessage: 'Hide'
  }
})

export interface AffordabilityCalculatorLoanDetailsSectionProps {
  loanType: string
  setLoanType: (loanType: string) => void
  setLoanTerm: (loanTerm: number) => void
  interestRate: string
  setInterestRate: (interestRate: string) => void
  setHasUsedAffordabilityCalculator: (hasUsedAffordabilityCalculator: boolean) => void
}

export const AffordabilityCalculatorLoanDetailsSection = ({
  loanType,
  setLoanType,
  setLoanTerm,
  interestRate,
  setInterestRate,
  setHasUsedAffordabilityCalculator
}: AffordabilityCalculatorLoanDetailsSectionProps) => {
  const intl = useIntl()
  const [showEditableFields, setShowEditableFields] = useState(false)

  return (
    <Card p={4}>
      <Button
        w='100%'
        variant='link'
        display='block'
        textAlign='left'
        fontWeight='inherit'
        textColor='currentColor'
        _hover={{ textDecoration: 'none' }}
        onClick={() => setShowEditableFields(!showEditableFields)}
        tracking={{
          guid: 'fWyVVuxYvtB2rzy8yndpFZ',
          ui_context: 'Affordability Calculator Page',
          descriptor: `Loan Details ${showEditableFields ? 'Hide' : 'Show'}`
        }}
      >
        <Flex align='center' justify='space-between'>
          <Heading as='h4' size={['2xs', 'xs', 'sm']}>
            {intl.formatMessage(MSG.loanDetails)}
          </Heading>
          <IconButton
            size='xs'
            variant='link'
            onClick={() => setShowEditableFields(!showEditableFields)}
            aria-label={intl.formatMessage(showEditableFields ? MSG.hide : MSG.show)}
            icon={<Icon height={2} name='play' transform={`rotate(${showEditableFields ? -90 : 90}deg)`} />}
            tracking={{
              guid: 'wZTS76hiueYXqTvSgKMg4F',
              ui_context: 'Affordability Calculator Page',
              descriptor: `Loan Details ${showEditableFields ? 'Hide' : 'Show'}`
            }}
          />
        </Flex>
      </Button>
      {showEditableFields && (
        <Flex mt={4} gap={4} direction='column' alignItems='flex-start'>
          <Flex width='100%' direction='column' gap={2} alignItems='flex-start'>
            <Flex gap={4} width='100%' direction={['column', null, null, 'row']}>
              <FormControl>
                <FormLabel size='xs'>{intl.formatMessage(MSG.loanType)}</FormLabel>
                <Select
                  size='sm'
                  value={loanType}
                  name='loanType'
                  onFocus={() => setHasUsedAffordabilityCalculator(true)}
                  onChange={e => {
                    const loanType = e.target.value
                    switch (loanType) {
                      case '30-fixed':
                        setLoanTerm(30)
                        localStorage?.setItem('affordabilityCalculatorLoanTerm', '30')
                        break
                      case '15-fixed':
                        setLoanTerm(15)
                        localStorage?.setItem('affordabilityCalculatorLoanTerm', '15')
                        break
                    }

                    setLoanType(loanType)
                    localStorage?.setItem('affordabilityCalculatorLoanType', loanType)
                  }}
                  tracking={{
                    guid: 'avMFVhYCDVqVR2WzZGjHCn',
                    ui_context: 'Affordability Calculator Page',
                    descriptor: 'Loan type select'
                  }}
                >
                  <option value='30-fixed'>{intl.formatMessage(MSG.loanProgram30Fixed)}</option>
                  <option value='15-fixed'>{intl.formatMessage(MSG.loanProgram15Fixed)}</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel size='xs'>{intl.formatMessage(MSG.interestRate)}</FormLabel>
                <InputGroup size='sm'>
                  <Input
                    size='sm'
                    type='number'
                    value={interestRate || ''}
                    name='interestRate'
                    onFocus={() => setHasUsedAffordabilityCalculator(true)}
                    onChange={e => {
                      setInterestRate(e.target.value)
                      localStorage?.setItem('affordabilityCalculatorInterestRate', e.target.value)
                    }}
                    tracking={{
                      guid: '8KmEhj4ZuKUHoV9ERdW476',
                      ui_context: 'Affordability Calculator Page',
                      descriptor: 'Interest rate input field'
                    }}
                  />
                  <InputRightElement pointerEvents='none' fontSize='xs' mr={-0.5}>
                    %
                  </InputRightElement>
                </InputGroup>
              </FormControl>
            </Flex>
          </Flex>
        </Flex>
      )}
    </Card>
  )
}
